import { useEffect, useMemo, useState } from "react";
import { AppLayout, RiskBanner } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Lock, Clock, Sparkles, TrendingUp, CheckCircle, ArrowUpRight, Wallet } from "lucide-react";
import { toast } from "sonner";

function fmt(v, d = 2) {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

function formatDuration(secs) {
  if (secs <= 0) return "Unlocked · claim now";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const PLAN_COLORS = ["#22C55E", "#00E5FF", "#EAB308"]; // Silver, Gold, Diamond

export default function Staking() {
  const { user, refresh } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [plans, setPlans] = useState([]);
  const [durationDays, setDurationDays] = useState(365);
  const [totals, setTotals] = useState({ active_count: 0, staked_principal: 0, pending_rewards: 0 });
  const [stakes, setStakes] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = async () => {
    try {
      const { data: p } = await api.get("/staking/plans");
      setEnabled(!!p.enabled);
      setPlans(p.plans || []);
      setDurationDays(p.duration_days || 365);
      if (!selectedPlanId && p.plans?.length) setSelectedPlanId(p.plans[0].id);
      const { data: m } = await api.get("/staking/my");
      setStakes(m.stakes || []);
      setTotals(m.totals || totals);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);

  const selected = useMemo(() => plans.find(p => p.id === selectedPlanId) || plans[0], [plans, selectedPlanId]);
  const balance = Number(user?.balance || 0);
  const amt = parseFloat(amount);
  const validAmount = selected && amt >= selected.min_amount && amt <= selected.max_amount && amt <= balance;
  const projectedTotal = selected && amt > 0 ? amt * selected.multiplier : 0;
  const projectedReward = projectedTotal - (amt || 0);

  const stake = async () => {
    if (!selected || !validAmount) return;
    setBusy(true);
    try {
      const { data } = await api.post("/staking/stake", { plan_id: selected.id, amount: amt });
      toast.success(data.message);
      setAmount("");
      await load();
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const claim = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/staking/claim/${id}`);
      toast.success(data.message);
      await load();
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!enabled) {
    return (
      <AppLayout variant="user">
        <div className="nb-card p-8 text-center">
          <Lock size={32} className="text-slate-500 mx-auto mb-3"/>
          <h2 className="font-heading text-white text-xl">Staking is temporarily paused</h2>
          <p className="text-slate-400 mt-1">Please check back soon.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout variant="user">
      <RiskBanner/>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-nb-cyan">Long-Term Staking</div>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-1">Stake USDT · Unlock a Bigger Return</h1>
        <p className="text-slate-400 mt-2 max-w-3xl">Lock your USDT for <span className="text-white font-mono">{durationDays} days</span>. The AI trading engine works on your capital every day, and rewards accumulate in your Staking Wallet. Withdraw <span className="text-white">principal + full reward</span> together the moment your stake unlocks.</p>
      </div>

      {/* Wallet summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Main Wallet"       value={`$${fmt(balance)}`}                            icon={Wallet}       color="#8AE234"/>
        <SummaryCard label="Currently Staked"  value={`$${fmt(totals.staked_principal)}`}            icon={Lock}         color="#00E5FF"/>
        <SummaryCard label="Rewards Pending"   value={`$${fmt(totals.pending_rewards)}`}             icon={Sparkles}     color="#EAB308"/>
        <SummaryCard label="Active Stakes"     value={`${totals.active_count}`}                       icon={TrendingUp}   color="#A855F7"/>
      </div>

      {/* Plan picker + stake form */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6" data-testid="staking-plans">
        {plans.map((p, i) => {
          const active = selected?.id === p.id;
          const color = PLAN_COLORS[i % PLAN_COLORS.length];
          return (
            <button
              key={p.id}
              data-testid={`stake-plan-${p.id}`}
              onClick={() => setSelectedPlanId(p.id)}
              className={`text-left rounded-2xl border p-5 transition-all ${active ? "border-nb-cyan bg-nb-cyan/5 shadow-[0_0_30px_rgba(0,229,255,0.15)]" : "border-white/10 bg-[#0F1626] hover:border-white/20"}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest" style={{color}}>Plan {p.id}</div>
                {active && <CheckCircle size={16} className="text-nb-cyan"/>}
              </div>
              <div className="font-heading text-white text-xl mt-1">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-mono text-4xl font-bold" style={{color}}>{p.multiplier}×</span>
                <span className="text-slate-400 text-xs">total return</span>
              </div>
              <div className="mt-4 space-y-1.5 text-xs">
                <Row lbl="Investment" val={`$${fmt(p.min_amount,0)} – $${fmt(p.max_amount,0)}`}/>
                <Row lbl="Duration"   val={`${p.duration_days || durationDays} days`}/>
                <Row lbl="Withdraw"   val="After full cycle"/>
              </div>
            </button>
          );
        })}
      </div>

      {/* Stake form */}
      {selected && (
        <div className="nb-card p-6 mb-6" data-testid="stake-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-nb-cyan">Stake into</div>
              <div className="font-heading text-white text-2xl mt-1">{selected.name} · {selected.multiplier}×</div>
              <p className="text-slate-400 text-sm mt-1">Investment range ${fmt(selected.min_amount,0)} – ${fmt(selected.max_amount,0)}. Locked for {selected.duration_days || durationDays} days. Rewards accumulate in the Staking Wallet.</p>

              <label className="block text-xs uppercase tracking-widest text-slate-400 mt-6">Amount (USDT)</label>
              <input
                data-testid="stake-amount"
                type="number"
                step="0.01"
                min={selected.min_amount}
                max={selected.max_amount}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={`Between $${selected.min_amount} and $${selected.max_amount}`}
                className="nb-input mt-1"
              />
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => setAmount(String(selected.min_amount))} className="text-[11px] px-3 py-1 rounded-full border border-white/10 text-slate-300 hover:text-white">Min</button>
                <button type="button" onClick={() => setAmount(String(Math.min(selected.max_amount, Math.floor(balance))))} className="text-[11px] px-3 py-1 rounded-full border border-nb-cyan/40 text-nb-cyan hover:bg-nb-cyan/10">Max ({fmt(Math.min(selected.max_amount, balance))})</button>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Main wallet balance: <span className="text-slate-300 font-mono">${fmt(balance)}</span></div>
            </div>

            {/* Projection card */}
            <div className="rounded-2xl border border-nb-cyan/20 bg-gradient-to-br from-[#0A1626] to-[#070B14] p-5">
              <div className="text-xs uppercase tracking-widest text-nb-cyan">Your Projected Payout</div>
              <div className="mt-4 space-y-3">
                <ProjRow lbl="You stake"          val={`$${fmt(amt || 0)}`}      big={false}/>
                <ProjRow lbl="Reward (365d)"      val={`+ $${fmt(projectedReward)}`} color="#8AE234"/>
                <div className="border-t border-white/10 pt-3"/>
                <ProjRow lbl="Total at unlock"    val={`$${fmt(projectedTotal)}`}    big color="#EAB308"/>
              </div>
              <button
                onClick={stake}
                disabled={!validAmount || busy}
                data-testid="stake-submit"
                className="mt-5 w-full py-3 rounded-xl font-heading font-bold text-black bg-nb-cyan disabled:bg-white/10 disabled:text-slate-500 hover:opacity-90 transition-all"
              >
                {busy ? "Processing…" : "Lock & Stake"}
              </button>
              <div className="text-[10px] text-slate-500 mt-2 text-center">Principal + full reward unlocks after {selected.duration_days || durationDays} days.</div>
            </div>
          </div>
        </div>
      )}

      {/* My stakes */}
      <div className="nb-card p-6" data-testid="my-stakes">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-white text-lg">My Stakes</div>
          <div className="text-xs text-slate-500 uppercase tracking-widest">Refreshes live · {new Date(now).toLocaleTimeString()}</div>
        </div>
        {stakes.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No stakes yet. Start with a plan above to see your first stake here.</div>}
        <div className="space-y-3">
          {stakes.map(s => {
            const remaining = s.remaining_seconds;
            const statusColor = s.status === "completed" ? "#8AE234" : (s.status === "cancelled" ? "#F87171" : "#00E5FF");
            const progressPct = Math.max(0, Math.min(100, ((s.duration_days * 86400 - remaining) / (s.duration_days * 86400)) * 100));
            return (
              <div key={s.id} data-testid={`stake-${s.id}`} className="rounded-xl border border-white/5 bg-[#0A1120] p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest" style={{color: statusColor}}>{s.status} · Plan {s.plan_id} · {s.multiplier}×</div>
                    <div className="font-mono text-white text-xl">${fmt(s.principal)} → <span style={{color: statusColor}}>${fmt(s.total_return)}</span></div>
                    <div className="text-[11px] text-slate-500 mt-0.5 font-mono">Staked: {new Date(s.staked_at).toLocaleDateString()} · Unlocks: {new Date(s.unlocks_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-xs text-slate-400 justify-end"><Clock size={12}/> Remaining</div>
                    <div className="font-mono text-nb-cyan text-lg" data-testid={`stake-remaining-${s.id}`}>{formatDuration(remaining)}</div>
                    {s.is_unlockable && (
                      <button onClick={() => claim(s.id)} disabled={busy} data-testid={`claim-${s.id}`} className="mt-2 px-4 py-1.5 rounded-full bg-[#8AE234] text-black text-xs font-bold hover:opacity-90"><ArrowUpRight size={12} className="inline mr-1"/> Claim ${fmt(s.total_return)}</button>
                    )}
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full transition-all" style={{width: `${progressPct}%`, background: `linear-gradient(90deg, #00E5FF, ${statusColor})`}}/>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-[10px] text-slate-500 uppercase tracking-widest">
                  <div><span className="text-slate-300 font-mono normal-case">${fmt(s.reward)}</span> reward</div>
                  <div>{progressPct.toFixed(1)}% complete</div>
                  <div className="text-right">{s.duration_days}d cycle</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  return (
    <div className="nb-card p-4 relative overflow-hidden">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono font-bold text-xl mt-1" style={{color}}>{value}</div>
      <Icon size={40} className="absolute right-2 top-2 opacity-10" style={{color}}/>
    </div>
  );
}

function Row({ lbl, val }) {
  return (<div className="flex items-center justify-between"><span className="text-slate-500">{lbl}</span><span className="text-slate-200 font-mono">{val}</span></div>);
}

function ProjRow({ lbl, val, color, big }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${big?"text-white":"text-slate-400"} text-sm`}>{lbl}</span>
      <span className={`font-mono ${big?"text-xl font-bold":""}`} style={{color: color || "#F8FAFC"}}>{val}</span>
    </div>
  );
}
